/**
 * Accès à l'API. Aucune donnée n'est fabriquée ici : tout vient du serveur.
 */

import type {
  Brouillon2027,
  DefinitionScenario,
  FormuleSalariale,
  NaturePoste,
  PropositionSalariale,
  HypotheseSalariale,
  ParametresSimulateur,
  TableauDeBord,
} from '@scc/core';
import type { DefinitionReglage } from '@scc/modules';

export interface Fraicheur {
  readonly derniereIngestion: string | null;
  readonly origine: string | null;
  /** Vrai quand aucune ingestion des sources n'a réussi : instantané livré. */
  readonly modeDegrade: boolean;
  /** Vrai quand une source seulement a répondu lors de la dernière campagne. */
  readonly partielle: boolean;
  /** Périodicité attendue des campagnes d'ingestion, en heures. */
  readonly periodeHeures: number;
  readonly ageHeures: number | null;
  /** Vrai quand la dernière campagne tient dans la fenêtre attendue. */
  readonly aJour: boolean;
}

export type Charge = TableauDeBord & { readonly fraicheur: Fraicheur };

export interface Identite {
  readonly authentifie: boolean;
  readonly courriel?: string;
  readonly nom?: string;
  readonly role?: 'lecture' | 'administration';
  readonly connexion?: string;
  /** Session lue mais refusée : page « accès réservé », sans redirection. */
  readonly refus?: { readonly message: string; readonly accueil: string | null };
}

export class ErreurApi extends Error {
  constructor(
    message: string,
    readonly statut: number,
    readonly connexion?: string,
    readonly accueil?: string,
  ) {
    super(message);
    this.name = 'ErreurApi';
  }
}

async function lire<T>(chemin: string): Promise<T> {
  const r = await fetch(chemin, { headers: { accept: 'application/json' }, credentials: 'same-origin' });
  if (!r.ok) {
    const corps = (await r.json().catch(() => ({}))) as {
      erreur?: string;
      connexion?: string;
      accueil?: string;
    };
    throw new ErreurApi(corps.erreur ?? `Erreur ${r.status}`, r.status, corps.connexion, corps.accueil);
  }
  return (await r.json()) as T;
}

export const session = (): Promise<Identite> =>
  lire<Identite>('/api/session').catch((e: unknown) => {
    if (e instanceof ErreurApi && e.statut === 401) {
      return { authentifie: false, connexion: e.connexion ?? '/connexion' };
    }
    if (e instanceof ErreurApi && e.statut === 403) {
      return { authentifie: false, refus: { message: e.message, accueil: e.accueil ?? null } };
    }
    throw e;
  });

export const tableauDeBord = (
  params?: Partial<ParametresSimulateur>,
  trajectoire?: string,
): Promise<Charge> => {
  const q = versRequete(params);
  const t = trajectoire ? `${q === '' ? '?' : '&'}trajectoire=${encodeURIComponent(trajectoire)}` : '';
  return lire<Charge>(`/api/tableau-de-bord${q}${t}`);
};

export interface DetailEcritures {
  readonly poste: string;
  readonly nb: number;
  readonly total: number;
  readonly ecritures: readonly {
    readonly id: string;
    readonly date: string;
    readonly montant: number;
    readonly sens: 'debit' | 'credit';
    readonly libelle: string;
    readonly compteLibelle: string;
    readonly exerciceAffectation: number | null;
    /** Origine du classement (`regle`, `jev`, `arbitrage`, `aucune`) ; absent si le module Classement est inactif. */
    readonly source?: string;
  }[];
}

/** `sens` départage un poste qui existe en produit et en charge ; omis, les deux sens sont servis. */
export const ecritures = (poste: string, sens?: 'recette' | 'depense'): Promise<DetailEcritures> =>
  lire<DetailEcritures>(`/api/ecritures/${encodeURIComponent(poste)}${sens ? `?sens=${sens}` : ''}`);

export interface SimulationEnregistree {
  readonly id: string;
  readonly nom: string;
  readonly parametres: ParametresSimulateur;
}

export async function enregistrerSimulation(
  nom: string,
  parametres: ParametresSimulateur,
): Promise<SimulationEnregistree & { lien: string }> {
  const r = await fetch('/api/simulations', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ nom, parametres }),
  });
  if (!r.ok) throw new ErreurApi('Enregistrement refusé.', r.status);
  return (await r.json()) as SimulationEnregistree & { lien: string };
}

export const chargerSimulation = (id: string): Promise<SimulationEnregistree> =>
  lire<SimulationEnregistree>(`/api/simulations/${encodeURIComponent(id)}`);

export interface JournalAdministration {
  /** Appels à Jev depuis minuit UTC ; ils sont exclus de `appels` (Zoho, Directus). */
  readonly jev: { readonly appelsDuJour: number; readonly coutDuJour: number };
  readonly appels: readonly {
    readonly horodatage: string;
    readonly source: string;
    readonly ressource: string;
    readonly statut: number | null;
    readonly duree_ms: number;
    readonly nb_elements: number;
    readonly erreur: string | null;
  }[];
  readonly campagnes: readonly {
    readonly id: number;
    readonly debut: string;
    readonly fin: string | null;
    readonly origine: string;
    readonly succes: number | null;
    readonly message: string | null;
  }[];
  readonly fraicheur: Fraicheur;
  readonly anomalies: TableauDeBord['anomalies'];
}

export const journalAdministration = (): Promise<JournalAdministration> =>
  lire<JournalAdministration>('/api/admin/journal');

export async function relancerIngestion(): Promise<{ succes: boolean; message: string }> {
  const r = await fetch('/api/admin/ingestion', { method: 'POST', credentials: 'same-origin' });
  if (!r.ok) throw new ErreurApi('Réingestion refusée.', r.status);
  return (await r.json()) as { succes: boolean; message: string };
}

export async function deconnecter(): Promise<string | null> {
  const r = await fetch('/api/deconnexion', { method: 'POST', credentials: 'same-origin' });
  const corps = (await r.json().catch(() => ({}))) as { redirection?: string | null };
  return corps.redirection ?? null;
}

function versRequete(params?: Partial<ParametresSimulateur>): string {
  if (!params) return '';
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) q.set(k, String(v));
  }
  const s = q.toString();
  return s === '' ? '' : `?${s}`;
}

/** Arbitre une ligne budgétaire : le montant saisi prime sur son inducteur. */
export async function arbitrerLigne(l: {
  poste: string;
  exercice: number;
  scenario: string;
  montant: number;
  commentaire?: string;
}): Promise<void> {
  const r = await fetch('/api/budget/ligne', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(l),
  });
  if (!r.ok) throw new ErreurApi('Arbitrage refusé.', r.status);
}

// ── Atterrissage de l'exercice en cours (décision 45) ──────────────────────

async function envoyer(chemin: string, methode: 'PUT' | 'POST' | 'DELETE', corps?: unknown): Promise<void> {
  const r = await fetch(chemin, {
    method: methode,
    headers: corps === undefined ? {} : { 'content-type': 'application/json' },
    credentials: 'same-origin',
    ...(corps === undefined ? {} : { body: JSON.stringify(corps) }),
  });
  if (!r.ok) {
    const e = (await r.json().catch(() => ({}))) as { erreur?: string };
    throw new ErreurApi(e.erreur ?? 'Modification refusée.', r.status);
  }
}

/** Estime le reste à engager d'un poste ; `null` revient au calcul (budget moins réalisé). */
export async function estimerResteAEngager(poste: string, montant: number | null, commentaire?: string | null): Promise<void> {
  await envoyer('/api/atterrissage/reste', 'PUT', { poste, montant, commentaire: commentaire ?? null });
}

export interface DepensePrevueSaisie {
  readonly poste: string;
  readonly libelle: string;
  readonly montant: number;
  readonly commentaire: string | null;
}

export async function ajouterDepensePrevue(d: DepensePrevueSaisie): Promise<void> {
  await envoyer('/api/atterrissage/depense', 'POST', d);
}

export async function modifierDepensePrevue(id: string, d: DepensePrevueSaisie): Promise<void> {
  await envoyer(`/api/atterrissage/depense/${encodeURIComponent(id)}`, 'PUT', d);
}

export async function supprimerDepensePrevue(id: string): Promise<void> {
  await envoyer(`/api/atterrissage/depense/${encodeURIComponent(id)}`, 'DELETE');
}

/** Efface les arbitrages d'un scénario : les inducteurs reprennent la main. */
export async function reinitialiserLigne(scenario: string): Promise<void> {
  const r = await fetch(`/api/budget/scenario/${encodeURIComponent(scenario)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!r.ok) throw new ErreurApi('Réinitialisation refusée.', r.status);
}

// ── Scénarios dérivés ──────────────────────────────────────────────────────

export interface ScenarioEnregistre {
  readonly cle: string;
  readonly libelle: string;
  readonly parent: string | null;
  readonly creeLe: string;
  readonly creePar: string;
  readonly modifieLe: string;
  readonly modifiePar: string;
  readonly definition: DefinitionScenario;
}

export const listerScenarios = (): Promise<{ scenarios: readonly ScenarioEnregistre[] }> =>
  lire<{ scenarios: readonly ScenarioEnregistre[] }>('/api/scenarios');

/** Message d'erreur du serveur, ou un message générique. */
async function echec(r: Response, parDefaut: string): Promise<never> {
  const corps = (await r.json().catch(() => ({}))) as { erreur?: string };
  throw new ErreurApi(corps.erreur ?? parDefaut, r.status);
}

export async function creerScenario(d: DefinitionScenario): Promise<void> {
  const r = await fetch('/api/scenarios', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(d),
  });
  if (!r.ok) await echec(r, 'Création refusée.');
}

export async function enregistrerScenario(d: DefinitionScenario): Promise<void> {
  const r = await fetch(`/api/scenarios/${encodeURIComponent(d.cle)}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(d),
  });
  if (!r.ok) await echec(r, 'Enregistrement refusé.');
}

export async function supprimerScenario(cle: string): Promise<void> {
  const r = await fetch(`/api/scenarios/${encodeURIComponent(cle)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!r.ok) await echec(r, 'Suppression refusée.');
}

// ── Trajectoires salariales par salarié ────────────────────────────────────

export async function enregistrerHypotheses(
  trajectoire: string,
  hypotheses: readonly HypotheseSalariale[],
): Promise<void> {
  const r = await fetch('/api/admin/hypotheses', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ trajectoire, hypotheses }),
  });
  if (!r.ok) await echec(r, 'Enregistrement refusé.');
}

export async function effacerHypotheses(trajectoire: string): Promise<void> {
  const r = await fetch(`/api/admin/hypotheses/${encodeURIComponent(trajectoire)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!r.ok) await echec(r, 'Remise à zéro refusée.');
}

// ── Effectif salarié ───────────────────────────────────────────────────────

export interface SalarieSaisi {
  readonly nom: string;
  readonly prenom: string;
  readonly role: 'direction' | 'rh_compta' | 'salarie';
  readonly titre: string | null;
  readonly dateEntree: string;
  readonly dateSortie: string | null;
  readonly brutMensuel: number;
  readonly groupe: number | null;
  readonly echelon: number | null;
}

export async function creerSalarie(s: SalarieSaisi): Promise<{ id: string; nbPoints: number }> {
  const r = await fetch('/api/admin/salaries', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(s),
  });
  if (!r.ok) await echec(r, 'Création refusée.');
  return (await r.json()) as { id: string; nbPoints: number };
}

export async function enregistrerSalarie(id: string, s: SalarieSaisi): Promise<void> {
  const r = await fetch(`/api/admin/salaries/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(s),
  });
  if (!r.ok) await echec(r, 'Enregistrement refusé.');
}

export async function supprimerSalarie(id: string): Promise<void> {
  const r = await fetch(`/api/admin/salaries/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!r.ok) await echec(r, 'Suppression refusée.');
}

// ── Modules ──────────────────────────────────────────────────────────────

export interface EntreeNavigationServie {
  readonly cle: string;
  readonly libelle: string;
  readonly picto: string;
  readonly groupe: string;
  readonly ordre: number;
  readonly droit: string;
  readonly module: string;
}

export interface ModulesServis {
  readonly modules: readonly { readonly id: string; readonly nom: string }[];
  readonly navigation: readonly EntreeNavigationServie[];
  /** Vide pour une session en lecture : les réglages ne sont servis qu'à l'administration. */
  readonly reglages: Readonly<Record<string, Readonly<Record<string, string | number | boolean>>>>;
}

export const modules = (): Promise<ModulesServis> => lire<ModulesServis>('/api/modules');

export interface ReglageAdmin {
  readonly cle: string;
  readonly definition: DefinitionReglage;
  readonly valeur: string | number | boolean;
  readonly surcharge: boolean;
}

export interface ModuleAdmin {
  readonly id: string;
  readonly nom: string;
  readonly description: string;
  readonly famille: string;
  readonly desactivable: boolean;
  readonly dependances: readonly string[];
  readonly dependants: readonly string[];
  readonly actifDemande: boolean;
  readonly actif: boolean;
  readonly reglages: readonly ReglageAdmin[];
  readonly modifiePar: string | null;
  readonly modifieLe: string | null;
}

export const adminModules = (): Promise<{ modules: readonly ModuleAdmin[] }> =>
  lire<{ modules: readonly ModuleAdmin[] }>('/api/admin/modules');

export async function modifierModule(
  id: string,
  modif: { actif?: boolean; reglages?: Record<string, string | number | boolean | null> },
): Promise<void> {
  const r = await fetch(`/api/admin/modules/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(modif),
  });
  if (!r.ok) await echec(r, `Erreur ${r.status}`);
}

// ── Brouillon du budget 2027 ───────────────────────────────────────────────

export interface EtatBrouillon2027 {
  readonly proposition: PropositionSalariale;
  readonly brouillon: Brouillon2027;
  readonly formules: readonly { readonly cle: FormuleSalariale; readonly libelle: string }[];
  readonly changements: readonly { code: string; libelle: string; avant: number; apres: number }[];
  readonly postesSaisissables: readonly { code: string; libelle: string; nature: NaturePoste }[];
}

export interface LigneBrouillonSaisie {
  readonly poste: string;
  readonly libelle: string;
  readonly montant: number;
  readonly incertaine: boolean;
  readonly commentaire: string | null;
}

export const brouillon2027 = (): Promise<EtatBrouillon2027> => lire<EtatBrouillon2027>('/api/budget-2027');

async function envoyer2027(chemin: string, methode: string, corps?: unknown): Promise<unknown> {
  const r = await fetch(`/api/budget-2027${chemin}`, {
    method: methode,
    headers: corps === undefined ? {} : { 'content-type': 'application/json' },
    credentials: 'same-origin',
    ...(corps === undefined ? {} : { body: JSON.stringify(corps) }),
  });
  if (!r.ok) await echec(r, `Erreur ${r.status}`);
  return r.json();
}

export const choisirFormule2027 = (formule: FormuleSalariale): Promise<unknown> =>
  envoyer2027('/formule', 'PUT', { formule });

/** `montant` nul : le poste revient au montant du Simulateur. */
export const saisirPoste2027 = (poste: string, montant: number | null): Promise<unknown> =>
  envoyer2027('/saisie', 'PUT', { poste, montant });

export const ajouterLigne2027 = (l: LigneBrouillonSaisie): Promise<unknown> => envoyer2027('/ligne', 'POST', l);

export const modifierLigne2027 = (id: string, l: LigneBrouillonSaisie): Promise<unknown> =>
  envoyer2027(`/ligne/${encodeURIComponent(id)}`, 'PUT', l);

export const supprimerLigne2027 = (id: string): Promise<unknown> =>
  envoyer2027(`/ligne/${encodeURIComponent(id)}`, 'DELETE');

export const publier2027 = (): Promise<unknown> => envoyer2027('/publication', 'POST');
